import _ from 'underscore';

import { Log } from '@biteinc/common';
import { TimeHelper } from '@biteinc/core-react';
import { LoyaltyAuthEntryMethod, LoyaltyAuthMethod } from '@biteinc/enums';
import { Strings } from '@biteinc/localization';

import { BackboneEvents } from '~/app/js/backbone-events';
import { localizeStr } from '~/app/js/localization/localization';

import Errors from '../utils/errors';
import { GCNLoyaltyPhoneNumberView } from './gcn_loyalty_phone_number_view';
import { GCNLoyaltyProgramHeaderView } from './gcn_loyalty_program_header_view';
import { GCNPunchhRedemptionCodeView } from './gcn_punchh_redemption_code_view';
import { GCNView } from './gcn_view';

export const GCNLoyaltyBarcodeView = GCNView.extend({
  className: 'loyalty-barcode-view',

  template: _.template(`
    <div class="body">
      <div class="title"></div>
      <div class="image"></div>
      <div class="subtitle"></div>
      <div class="fallback-or-text-redemption-button"></div>
      <div class="fallback-button redemption-code-button"></div>
      <div class="fallback-or-text-phone-button"></div>
      <div class="fallback-button phone-button"></div>
    </div>
  `),

  initialize(options, ...args) {
    GCNView.prototype.initialize.apply(this, options ? [options, ...args] : args);

    this.listenTo(gcn.loyaltyManager, BackboneEvents.GCNLoyaltyManager.DidUpdateAuthGuest, () => {
      this.trigger(BackboneEvents.GCNLoyaltyBarcodeView.DidUpdateAuthGuest, this);
    });

    if (options) {
      this.hidePhoneFallback = options.hidePhoneFallback === true;
      this.hideRedemptionCodeFallback = options.hideRedemptionCodeFallback === true;
    }
  },

  handleAuthData(data) {
    gcn.menuView.dismissModalPopup();
    gcn.menuView.dismissStablePopup();

    if (!gcn.loyaltyManager.isPunchhDirect() && !gcn.loyaltyManager.isPunchhOlo()) {
      if (gcn.loyaltyManager.loyaltyUsesTokenAuth()) {
        gcn.loyaltyManager.submitLoyaltyTokenData(data, LoyaltyAuthEntryMethod.BarcodeScanned);
        gcn.removeScannerHandler(this);
      } else {
        gcn.loyaltyManager.submitLoyaltyAuth(
          data,
          undefined,
          LoyaltyAuthEntryMethod.BarcodeScanned,

          (err) => {
            gcn.menuView.dismissSpinner();
            if (err) {
              const message = Errors.stringFromErrorCode(
                err.code,
                localizeStr(Strings.LOYALTY_AUTH_GENERIC_ERROR),
              );

              gcn.menuView.showSimpleAlert(message);

              return;
            }
          },
          LoyaltyAuthEntryMethod.BarcodeScanned,
        );
      }

      return;
    }

    if (gcn.loyaltyManager.isPunchhOlo()) {
      gcn.menuView.showSpinner(localizeStr(Strings.LOOKING_UP_ACCOUNT));
      gcn.loyaltyManager.submitLoyaltyAuth(
        data,
        undefined,
        LoyaltyAuthEntryMethod.BarcodeScanned,
        () => {
          gcn.menuView.dismissSpinner();
        },
      );
      gcn.removeScannerHandler(this);

      return;
    }

    gcn.loyaltyManager.handlePunchhLoyaltyData(data, LoyaltyAuthEntryMethod.BarcodeScanned);
    gcn.removeScannerHandler(this);
  },

  handleScannerData(scannerData) {
    Log.info('GCNLoyaltyBarcodeView.handleScannerData', scannerData);

    const loyaltyAuthData = gcn.loyaltyManager.getLoyaltyAuthData();
    const canScanAccountAfterRedemptionOrViceVersa =
      gcn.location.getLoyaltyIntegration().allowRedemptionCodeWithReward &&
      !(loyaltyAuthData?.tokenData && loyaltyAuthData?.redemptionCode);

    // If not validated, or either scan source is already processed, ignore scans.
    if (
      !gcn.orderManager.orderHasBeenValidated() ||
      (gcn.loyaltyManager.hasLoyaltyAuthData() && !canScanAccountAfterRedemptionOrViceVersa)
    ) {
      return;
    }

    // If the scanner data is too long it comes in as two separate events.
    // We receive the first half, and then the second ~ 5ms after.
    if (!this.firstHalf) {
      this.firstHalf = scannerData;
      this.sentFirstHalf = false;
      setTimeout(() => {
        if (this.firstHalf && !this.sentFirstHalf) {
          this.handleAuthData(this.firstHalf);
        }
      }, 75);
    } else {
      this.sentFirstHalf = true;
      this.handleAuthData(`${this.firstHalf}${scannerData}`);
    }
  },

  render() {
    const self = this;

    this.$el.html(this.template());

    this.$el.prepend(new GCNLoyaltyProgramHeaderView().render().$el);

    this._$title = this.$('.title');
    this._$subtitle = this.$('.subtitle');
    this._$phoneButton = this.$('.phone-button');
    this._$redemptionCodeButton = this.$('.redemption-code-button');

    this._$title.htmlOrText(localizeStr(Strings.LOYALTY_APP_AUTH_TITLE));
    this._$subtitle.htmlOrText(localizeStr(Strings.LOYALTY_APP_AUTH_SUBTITLE));

    // Add a barcode listener in here for loyalty
    gcn.pushScannerHandler(this);

    const fallbackAuthMethod = gcn.loyaltyManager.getFallbackLoyaltyAuthMethodToBarcode();
    // At this point, this flow only supports phone number fallback.
    if (fallbackAuthMethod === LoyaltyAuthMethod.PhoneNumber && !this.hidePhoneFallback) {
      const _$orText = this.$('.fallback-or-text-phone-button');
      _$orText.text('OR');
      _$orText.css('opacity', '0');

      this._$phoneButton.htmlOrText(localizeStr(Strings.LOYALTY_APP_AUTH_PHONE_FALLBACK));
      this._$phoneButton.css('opacity', '0');
      setTimeout(() => {
        _$orText.css('opacity', '1');
        self._$phoneButton.css('opacity', '1');
      }, TimeHelper.SECOND);

      this._$phoneButton.onButtonTapOrHold('lbvPhone', () => {
        // TODO: Clean up self-dismissal with a controller.
        gcn.menuView.dismissStablePopup();
        // Remove barcode listener
        gcn.removeScannerHandler(this);
        const loyaltyPhoneNumberView = new GCNLoyaltyPhoneNumberView({
          callback() {
            gcn.menuView.dismissStablePopup();
          },
          lookupUserMethod(phoneNumber) {
            gcn.loyaltyManager.extendLoyaltyAuthWithTokenData(
              phoneNumber,
              LoyaltyAuthEntryMethod.PhoneNumberManuallyEntered,
            );
            // Test Level Up number.
            // gcn.loyaltyManager.submitLoyaltyTokenData(
            //   '3947855938',
            //   LoyaltyAuthEntryMethod.PhoneNumberManuallyEntered,
            // );
          },
        });
        gcn.menuView.showStablePopup(loyaltyPhoneNumberView, 'phone-number-view');
      });
    } else {
      this._$phoneButton.remove();
    }

    if (gcn.loyaltyManager.isPunchhDirect() && !this.hideRedemptionCodeFallback) {
      const _$orText = this.$('.fallback-or-text-redemption-button');
      _$orText.text('OR');
      _$orText.css('opacity', '0');

      this.$el.addClass('supports-redemption-codes');
      this._$redemptionCodeButton.htmlOrText(
        localizeStr(Strings.LOYALTY_APP_REDEMPTION_CODE_FALLBACK),
      );
      this._$redemptionCodeButton.css('opacity', '0');
      setTimeout(() => {
        self._$redemptionCodeButton.css('opacity', '1');
        _$orText.css('opacity', '1');
      }, TimeHelper.SECOND);
    } else {
      this._$redemptionCodeButton.remove();
    }

    this._$redemptionCodeButton.onButtonTapOrHold('lbvRedemption', () => {
      // TODO: Clean up self-dismissal with a controller.
      gcn.menuView.dismissStablePopup();
      const redemptionCodeView = new GCNPunchhRedemptionCodeView({
        callback() {
          gcn.menuView.dismissStablePopup();
          gcn.removeScannerHandler(this);
        },
      });
      gcn.menuView.showStablePopup(redemptionCodeView, 'punchh-redemption-code-view');
    });

    if (window.isKioskPreview) {
      setTimeout(() => {
        const scannerData = prompt('Please enter a barcode:', '');
        if (scannerData) {
          this.handleScannerData(scannerData);
        }
      }, 1000);
    }

    return this;
  },
});
